import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, Trophy, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';
import { API_URL } from '../utils/api';

// Ticket colors
const TICKET_COLORS = {
  green: '#22c55e',
  blue: '#3b82f6',
  pink: '#ec4899',
  yellow: '#eab308',
  orange: '#f97316',
  red: '#ef4444',
  purple: '#a855f7',
  white: '#f1f5f9',
};

const getColorHex = (colorName) => {
  return TICKET_COLORS[colorName?.toLowerCase()] || '#6b7280';
};

export default function CloakroomRaffleDisplayPage() {
  const { sessionCode } = useParams();
  const [session, setSession] = useState(null);
  const [currentWinner, setCurrentWinner] = useState(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [spinNumber, setSpinNumber] = useState(null);
  const [spinColor, setSpinColor] = useState('green');
  const [spinSerial, setSpinSerial] = useState('');
  const lastRoundRef = useRef(0);
  const spinIntervalRef = useRef(null);

  useEffect(() => {
    if (!sessionCode) return;

    const fetchSession = async () => {
      try {
        const res = await fetch(`${API_URL}/api/raffle/cloakroom/sessions/${sessionCode}`);
        if (res.ok) {
          const data = await res.json();
          setSession(data);

          // Check for new winner
          if (data.prize_rounds?.length > lastRoundRef.current) {
            const newWinner = data.prize_rounds[data.prize_rounds.length - 1];
            lastRoundRef.current = data.prize_rounds.length;
            
            // Start reveal animation
            revealWinner(newWinner, data.books);
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      }
    };

    fetchSession();
    const interval = setInterval(fetchSession, 1000);

    return () => {
      clearInterval(interval);
      if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
    };
  }, [sessionCode]);

  const revealWinner = (winner, books) => {
    setIsRevealing(true);
    setCurrentWinner(null);

    // Build spin pool from books
    const spinPool = [];
    books.forEach(book => {
      for (let i = book.start_number; i <= Math.min(book.start_number + 100, book.end_number); i++) {
        spinPool.push({ number: i, color: book.color, serial: book.serial });
      }
    });

    // Spin animation
    let count = 0;
    const maxSpins = 30;
    
    spinIntervalRef.current = setInterval(() => {
      const random = spinPool[Math.floor(Math.random() * spinPool.length)];
      setSpinNumber(random.number);
      setSpinColor(random.color);
      setSpinSerial(random.serial);
      count++;

      if (count >= maxSpins) {
        clearInterval(spinIntervalRef.current);
        
        // Show the actual winner
        setTimeout(() => {
          setCurrentWinner(winner);
          setIsRevealing(false);
          
          // Celebration!
          const colorHex = getColorHex(winner.winning_ticket.color);
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 },
            colors: [colorHex, '#fbbf24', '#ffffff']
          });
          
          // Second burst
          setTimeout(() => {
            confetti({
              particleCount: 100,
              angle: 60,
              spread: 55,
              origin: { x: 0 },
              colors: [colorHex, '#fbbf24']
            });
            confetti({
              particleCount: 100,
              angle: 120,
              spread: 55,
              origin: { x: 1 },
              colors: [colorHex, '#fbbf24']
            });
          }, 300);
        }, 500);
      }
    }, 80);
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Ticket className="w-20 h-20 mx-auto text-amber-400 animate-pulse mb-4" />
          <p className="text-slate-400 text-xl">Connecting to raffle...</p>
          <p className="text-slate-400 mt-2">Session: {sessionCode}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-8 overflow-hidden">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-2 flex items-center justify-center gap-3">
          <Ticket className="w-10 h-10 text-amber-400" />
          Cloakroom Raffle
        </h1>
        <p className="text-slate-400 text-lg">
          {session.remaining_tickets} tickets remaining • {session.prize_rounds?.length || 0} prizes drawn
        </p>
      </div>

      {/* Main Display */}
      <AnimatePresence mode="wait">
        {isRevealing ? (
          // Spinning animation
          <motion.div
            key="spinning"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            className="relative"
          >
            <motion.div
              animate={{ 
                rotate: [0, 5, -5, 5, -5, 0],
                scale: [1, 1.02, 0.98, 1.02, 0.98, 1]
              }}
              transition={{ duration: 0.2, repeat: Infinity }}
              className="rounded-3xl p-10 shadow-2xl"
              style={{ backgroundColor: getColorHex(spinColor) }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-inner min-w-[300px]">
                <p className="text-slate-600 font-mono text-2xl mb-2 text-center">
                  {spinSerial}
                </p>
                <motion.p 
                  key={spinNumber}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-9xl font-bold text-slate-900 text-center"
                >
                  {spinNumber}
                </motion.p>
              </div>
            </motion.div>
            <Sparkles className="absolute -top-6 -right-6 w-16 h-16 text-amber-400 animate-pulse" />
            <Sparkles className="absolute -bottom-6 -left-6 w-16 h-16 text-amber-400 animate-pulse" />
          </motion.div>
        ) : currentWinner ? (
          // Winner display
          <motion.div
            key="winner"
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", bounce: 0.5 }}
            className="relative"
          >
            {/* Prize label */}
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="absolute -top-16 left-1/2 -translate-x-1/2 z-10"
            >
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-3xl font-bold px-8 py-3 rounded-full shadow-lg">
                {currentWinner.prize_label}
              </div>
            </motion.div>

            {/* Ticket */}
            <motion.div
              animate={{ 
                boxShadow: [
                  '0 0 30px rgba(251, 191, 36, 0.3)',
                  '0 0 60px rgba(251, 191, 36, 0.5)',
                  '0 0 30px rgba(251, 191, 36, 0.3)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="rounded-3xl p-12"
              style={{ backgroundColor: getColorHex(currentWinner.winning_ticket.color) }}
            >
              <div className="bg-white rounded-2xl p-10 shadow-inner min-w-[350px]">
                <p className="text-slate-600 font-mono text-3xl mb-3 text-center">
                  {currentWinner.winning_ticket.serial}
                </p>
                <p className="text-[10rem] font-bold text-slate-900 text-center leading-none">
                  {currentWinner.winning_ticket.number}
                </p>
              </div>
            </motion.div>

            {/* Trophy */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
              transition={{ delay: 0.5, type: "spring" }}
              className="absolute -top-8 -right-8"
            >
              <Trophy className="w-24 h-24 text-amber-400 drop-shadow-lg" />
            </motion.div>
          </motion.div>
        ) : (
          // Waiting state
          <motion.div
            key="waiting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {session.books.map((book, idx) => (
                <motion.div
                  key={idx}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className="rounded-xl p-4 text-center"
                  style={{ backgroundColor: `${getColorHex(book.color)}40` }}
                >
                  <div 
                    className="w-16 h-20 mx-auto rounded-lg mb-2 flex items-center justify-center"
                    style={{ backgroundColor: getColorHex(book.color) }}
                  >
                    <div className="bg-white rounded w-12 h-14 flex flex-col items-center justify-center">
                      <span className="text-xs text-slate-400 font-mono">{book.serial}</span>
                      <span className="text-lg font-bold">1-{book.end_number}</span>
                    </div>
                  </div>
                  <p className="text-white font-medium capitalize">{book.color}</p>
                  <p className="text-slate-400 text-sm">{book.ticket_count} tickets</p>
                </motion.div>
              ))}
            </div>
            <p className="text-3xl text-slate-400">Waiting for next draw...</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recent Winners */}
      {session.prize_rounds?.length > 0 && !isRevealing && (
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mt-12 w-full max-w-4xl"
        >
          <h2 className="text-xl text-slate-400 text-center mb-4">Previous Winners</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {[...session.prize_rounds].reverse().slice(currentWinner ? 1 : 0, 6).map((round, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 bg-slate-800/50 rounded-lg px-4 py-2"
              >
                <div 
                  className="w-8 h-10 rounded flex items-center justify-center"
                  style={{ backgroundColor: getColorHex(round.winning_ticket.color) }}
                >
                  <div className="bg-white rounded-sm w-6 h-7 flex items-center justify-center">
                    <span className="text-xs font-bold">{round.winning_ticket.number}</span>
                  </div>
                </div>
                <div>
                  <p className="text-amber-400 font-medium text-sm">{round.prize_label}</p>
                  <p className="text-slate-400 text-xs font-mono">{round.winning_ticket.serial}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
